import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { Check, Lock, Zap } from "lucide-react";

const ACCENTS = {
  free: { c: "#00E5FF", glow: "box-glow-cyan", border: "border-cyan/40", text: "text-cyan" },
  explorer: { c: "#22D3EE", glow: "box-glow-cyan", border: "border-cyan/60", text: "text-cyan" },
  premium: { c: "#FFB800", glow: "box-glow-amber", border: "border-amber/60", text: "text-amber" },
};

export default function Pricing() {
  const [tiers, setTiers] = useState([]);
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/content/tiers").then((r) => setTiers(r.data)).catch(() => {});
  }, []);

  const choose = async (tier) => {
    if (!user) {
      navigate("/auth");
      return;
    }
    if (tier.price === 0) {
      toast.info("The Initiate tier is already active on every account.");
      return;
    }
    if (user.tier === tier.id) {
      toast.info("This tier is already active.");
      return;
    }
    setBusy(tier.id);
    try {
      const res = await api.post("/billing/checkout", {
        tier_id: tier.id,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-app relative pt-16 pb-28" data-testid="pricing-page">
      <Seo
        title="Membership Tiers & Pricing"
        description="AETHER membership tiers — unlock more simulation environments, uncapped AI generations, premium personas and VFX-grade rendering. Free to start, no card required."
        path="/pricing"
      />
      <div className="absolute inset-0 hud-grid opacity-25" />
      <div className="relative mx-auto max-w-6xl px-5">
        <div className="text-center mb-14">
          <div className="font-mono text-[11px] tracking-[0.4em] text-cyan/70">ACCESS PROTOCOLS</div>
          <h1 className="font-head text-4xl sm:text-5xl font-bold uppercase mt-3">
            Choose Your <span className="text-amber text-glow-amber">Clearance</span>
          </h1>
          <p className="mt-4 text-slate-400">Unlock environments, raise generation limits, ascend the simulation.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((tier, i) => {
            const a = ACCENTS[tier.id] || ACCENTS.free;
            const current = user?.tier === tier.id;
            const featured = tier.id === "premium";
            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.12 }}
                className={`relative glass clip-chamfer p-8 flex flex-col border ${a.border} ${featured ? a.glow : ""}`}
                data-testid={`tier-card-${tier.id}`}
              >
                {featured && (
                  <div className="absolute -top-px right-6 font-mono text-[9px] tracking-widest px-3 py-1 bg-amber text-app">
                    APEX
                  </div>
                )}
                <div className="font-mono text-[10px] tracking-[0.25em] text-slate-500">{tier.tagline?.toUpperCase()}</div>
                <h2 className={`font-head text-2xl uppercase font-bold mt-1 ${a.text}`}>{tier.name}</h2>
                <div className="mt-5 flex items-end gap-1">
                  <span className="font-head text-5xl font-bold text-slate-100">${tier.price}</span>
                  <span className="text-slate-500 mb-2 font-mono text-xs">/cycle</span>
                </div>

                <div className="mt-6 space-y-3 flex-1">
                  {tier.features.map((f, idx) => (
                    <div key={idx} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: a.c }} />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => choose(tier)}
                  disabled={busy === tier.id || current}
                  data-testid={`tier-select-${tier.id}`}
                  className={`btn-hud mt-8 w-full py-3 text-sm border ${a.border} ${a.text} hover:text-app disabled:opacity-60`}
                  style={busy === tier.id ? {} : { ["--tw-hover"]: a.c }}
                  onMouseEnter={(e) => { if (!current) { e.currentTarget.style.background = a.c; e.currentTarget.style.color = "#030712"; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = a.c; }}
                >
                  {current ? "ACTIVE" : busy === tier.id ? "REDIRECTING..." : tier.price === 0 ? "Default Access" : "Upgrade"}
                </button>
              </motion.div>
            );
          })}
        </div>
        <p className="text-center font-mono text-[10px] text-slate-600 mt-10 tracking-widest">
          <Lock className="inline w-3 h-3 mr-1" /> SECURE CHECKOUT VIA STRIPE — TEST MODE
        </p>
      </div>
    </div>
  );
}
